import { useState } from 'react';

type Period = '5' | '10' | '15' | '20';

// Dataset fijo del S&P500 para evitar errores
const sp500Data = {
  '5': [100, 106, 118, 131, 141, 153],
  '10': [100, 106, 118, 131, 141, 153, 167, 183, 203, 216, 230],
  '15': [100, 106, 118, 131, 141, 153, 167, 183, 203, 216, 230, 255, 277, 306, 325, 353],
  '20': [100, 106, 118, 131, 141, 153, 167, 183, 203, 216, 230, 255, 277, 306, 325, 353, 389, 418, 467, 520, 553]
};

export default function SPGraphs() {
  const [selectedPeriod, setSelectedPeriod] = useState<Period>('10');
  const currentData = sp500Data[selectedPeriod];
  
  // Calcular valores máximo y mínimo para escalar la gráfica
  const maxValue = Math.max(...currentData);
  const minValue = Math.min(...currentData);
  const range = maxValue - minValue;

  // Generar puntos para la línea
  const points = currentData.map((value, index) => {
    const x = (index / (currentData.length - 1)) * 100;
    const y = 100 - ((value - minValue) / range) * 100;
    return `${x},${y}`;
  }).join(' ');

  // Generar path para el área
  const areaPath = `M 0,100 L ${currentData.map((value, index) => {
    const x = (index / (currentData.length - 1)) * 100;
    const y = 100 - ((value - minValue) / range) * 100;
    return `${x},${y}`;
  }).join(' L ')} L 100,100 Z`;

  return (
    <section id="sp-graphs" className="py-24 px-6 lg:px-12 bg-black">
      <div className="max-w-6xl mx-auto">
        {/* Título */}
        <div className="text-center mb-16 space-y-4">
          <h2 className="text-4xl md:text-5xl font-bold text-white">
            Así ha crecido el <span className="text-[#D4AF37]">S&amp;P500</span> en los últimos años
          </h2>
          <p className="text-xl text-[#C5C7D0]">
            Visualiza la tendencia real en distintos periodos.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex justify-center gap-4 mb-12 flex-wrap">
          {(['5', '10', '15', '20'] as Period[]).map((period) => (
            <button
              key={period}
              onClick={() => setSelectedPeriod(period)}
              className={`px-8 py-3 rounded-full font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                selectedPeriod === period
                  ? 'bg-[#D4AF37] text-black shadow-lg shadow-[#D4AF37]/50'
                  : 'bg-[#0A1A35] text-[#C5C7D0] hover:bg-[#0A1A35]/80 border border-[#C5C7D0]/30'
              }`}
            >
              {period} años
            </button>
          ))}
        </div>

        {/* Gráfica */}
        <div className="bg-gradient-to-br from-[#0A1A35] to-black rounded-2xl p-8 md:p-12 border border-[#D4AF37]/20 shadow-2xl">
          <div className="relative h-96 w-full">
            {/* Líneas guía horizontales */}
            <div className="absolute inset-0 flex flex-col justify-between">
              {[100, 75, 50, 25, 0].map((percent, idx) => (
                <div key={percent} className="relative w-full border-t border-[#C5C7D0]/20">
                  <span className="absolute -left-16 -top-3 text-[#C5C7D0]/60 text-sm font-mono">
                    ${Math.round(minValue + (range * percent / 100))}
                  </span>
                </div>
              ))}
            </div>

            {/* SVG con la gráfica */}
            <svg 
              className="absolute inset-0 w-full h-full overflow-visible" 
              viewBox="0 0 100 100" 
              preserveAspectRatio="none"
            >
              <defs>
                <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#A87E10" />
                  <stop offset="50%" stopColor="#D4AF37" />
                  <stop offset="100%" stopColor="#A87E10" />
                </linearGradient>
                <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.4" />
                  <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
                </linearGradient>
              </defs>
              
              {/* Área bajo la curva */}
              <path
                d={areaPath}
                fill="url(#areaGradient)"
              />
              
              {/* Línea principal */}
              <polyline
                points={points}
                fill="none"
                stroke="url(#lineGradient)"
                strokeWidth="0.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
              />

              {/* Puntos en la línea */}
              {currentData.map((value, index) => {
                const x = (index / (currentData.length - 1)) * 100;
                const y = 100 - ((value - minValue) / range) * 100;
                return (
                  <circle
                    key={index}
                    cx={x}
                    cy={y}
                    r="0.8"
                    fill="#D4AF37"
                    className="drop-shadow-lg"
                  />
                );
              })}
            </svg>

            {/* Etiquetas del eje X (años) */}
            <div className="absolute -bottom-8 left-0 right-0 flex justify-between px-2">
              {[0, Math.floor(currentData.length / 2), currentData.length - 1].map((idx) => (
                <span key={idx} className="text-[#C5C7D0]/60 text-sm">
                  Año {idx}
                </span>
              ))}
            </div>
          </div>

          {/* Texto informativo */}
          <div className="mt-12 text-center">
            <p className="text-[#C5C7D0] text-lg">
              Rendimiento histórico del S&amp;P500 en los últimos{' '}
              <strong className="text-[#D4AF37]">{selectedPeriod} años</strong>.
              <br />
              <span className="text-sm opacity-80">Datos de mercado referenciales.</span>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
